/* The compact layouts' icon set.

   A rail button is 44px with a seven-pixel label under it, and a tab bar's is not much more, so
   these carry the meaning at sizes where a word cannot. They are drawn rather than set as emoji —
   an emoji is a font glyph with its own metrics and its own colour, so it changes shape between
   platforms and cannot take the selected state's colour, which is the one thing these have to do.

   One style throughout: a 24 grid, 1.6 stroke, round caps and joins, no fills, `currentColor`. */

const Icon = ({ size = 20, children, title }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : "true"}
    role={title ? "img" : undefined}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
)

export const MapIcon = (props) => (
  <Icon {...props}>
    <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
    <path d="M9 4v13" />
    <path d="M15 6.5v13" />
  </Icon>
)

export const CitiesIcon = (props) => (
  <Icon {...props}>
    <path d="M3 21V10l5-3v14" />
    <path d="M8 21V4l7 3v14" />
    <path d="M15 21V9l6 3v9" />
  </Icon>
)

export const MovesIcon = (props) => (
  <Icon {...props}>
    <path d="M4 12h13" />
    <path d="m13 7 5 5-5 5" />
  </Icon>
)

export const TroopsIcon = (props) => (
  <Icon {...props}>
    <path d="M12 3 4 6v6c0 4.5 3.4 7.9 8 9 4.6-1.1 8-4.5 8-9V6l-8-3Z" />
    <path d="M9 12h6" />
    <path d="M12 9v6" />
  </Icon>
)

export const RadarIcon = (props) => (
  <Icon {...props}>
    <path d="M4 4h16v16H4z" />
    <path d="M10 4v16M16 4v16M4 10h16M4 16h16" />
  </Icon>
)

export const ReportIcon = (props) => (
  <Icon {...props}>
    <path d="M6 3h9l4 4v14H6V3Z" />
    <path d="M15 3v4h4" />
    <path d="M9 12h7" />
    <path d="M9 16h7" />
  </Icon>
)

export const CommsIcon = (props) => (
  <Icon {...props}>
    <path d="M20 12a7 7 0 0 1-7 7H8l-4 3v-5.6A7 7 0 0 1 6.5 6.4 7 7 0 0 1 13 5a7 7 0 0 1 7 7Z" />
  </Icon>
)

/* The fog-of-war preview: an eye with fog rolling over its lower half. */
/* The fog of war, as an eye (owner, 2026-10-26: "an open/closed eye instead, it's more clear"): open
   while the whole map is seen, closed while the fog hides what a player cannot see. It replaced a
   fog-bank glyph that read as nothing in particular. */
export const EyeIcon = ({ closed = false, ...props }) => (
  <Icon {...props}>
    {closed ? (
      <>
        <path d="M2.5 11c2.5 3.6 5.7 5.4 9.5 5.4s7-1.8 9.5-5.4" />
        <path d="m5.2 14.4-1.6 2.1M9.3 16.2l-.7 2.5M14.7 16.2l.7 2.5M18.8 14.4l1.6 2.1" />
      </>
    ) : (
      <>
        <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
        <circle cx="12" cy="12" r="3.2" />
        <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      </>
    )}
  </Icon>
)

/* The grid's coordinates (owner, 2026-10-26: "a number with a square, and when pressed, like the
   number has been tachado"): a cell with its number, struck through while the coordinates are off.
   It replaced 👁 / 🚫, which read as "visibility" and "forbidden" rather than as the grid. */
export const GridIcon = ({ off = false, ...props }) => (
  <Icon {...props}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
    <text x="12" y="16.4" textAnchor="middle" fontSize="11.5" fontWeight="700" fill="currentColor" stroke="none" fontFamily="inherit">
      12
    </text>
    {off ? <path d="M5.5 18.5 18.5 5.5" strokeWidth="2" /> : null}
  </Icon>
)

export const CloseIcon = (props) => (
  <Icon {...props}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Icon>
)

/* Who holds the clock. The roster used to say the word "host" beside the name, and at two seats to
   a row the word was most of the room a name had (owner, 2026-09-10: the name, this mark and the
   crowns, and nothing else).

   A star, and not the gear the wording used to carry ("host ⚙"). This is drawn at 12px beside a
   name, and a gear at that size is a circle with eight one-pixel spokes — on screen it read as a
   small sun. A filled star survives the size, which is the only test a 12px mark has to pass.
   Filled *and* stroked, so its points thicken rather than thinning into the row's text. */
export const HostIcon = (props) => (
  <Icon {...props}>
    <path
      fill="currentColor"
      d="M12 3.4 14.5 9.2 20.8 9.8 16 14 17.5 20.2 12 16.9 6.5 20.2 8 14 3.2 9.8 9.5 9.2Z"
    />
  </Icon>
)

/* The team behind the game, in the header beside the balance. A heart rather than two figures: it
   opens a page asking for support, and at 18px two heads are a smudge. */
export const TeamIcon = (props) => (
  <Icon {...props}>
    <path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z" />
  </Icon>
)

/* Handing the game's address on, in the team modal. The three joined nodes rather than the box
   with an arrow: the box is one platform's glyph and reads as "open elsewhere" on the others. */
export const ShareIcon = (props) => (
  <Icon {...props}>
    <circle cx="18" cy="5" r="2.6" />
    <circle cx="6" cy="12" r="2.6" />
    <circle cx="18" cy="19" r="2.6" />
    <path d="m8.3 10.7 7.4-4.3" />
    <path d="m8.3 13.3 7.4 4.3" />
  </Icon>
)

export default Icon
